'use client';

import { SHARE_STATE_TOKEN_QUERY_PARAM } from '@repo/map-user-data/share-state';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectMapCategories, selectMapData, setFilterStateFromUrl } from '@repo/store/maps/slices/mapData';
import { useCallback, useEffect, useState } from 'react';
import { getShareState } from '../../utils/share-state-client';
import { clearMapFilterUrlParams, parseMapFilterUrlState, type UrlFilterState } from '../../utils/share-url-filters';
interface UseFilterUrlInitializerOptions {
  hasStoredPreferences?: boolean;
  /** Whether the preference sync has completed (true = final value for hasStoredPreferences) */
  syncSettled?: boolean;
}
export function useFilterUrlInitializer(options?: UseFilterUrlInitializerOptions) {
  const dispatch = useAppDispatch();
  const mapCategories = useAppSelector(selectMapCategories);
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const [pendingUrlFilterState, setPendingUrlFilterState] = useState<UrlFilterState | null>(null);
  const parseFilterStateFromUrl = useCallback(async () => {
    if (typeof window === 'undefined') return null;
    try {
      const url = new URL(window.location.href);
      const shareStateToken = url.searchParams.get(SHARE_STATE_TOKEN_QUERY_PARAM);
      if (shareStateToken) {
        if (!externalMapId) return null;
        const shareState = await getShareState(shareStateToken);
        if (shareState.kind === 'mapFilter' && shareState.mapExternalId === externalMapId) {
          return shareState.filters;
        }
        return null;
      }
      return mapData ? parseMapFilterUrlState(url.searchParams, mapData) : null;
    } catch {
      return null;
    }
  }, [externalMapId, mapData]);
  const cleanUpFilterUrlParams = useCallback(() => {
    if (typeof window === 'undefined') return;
    const url_0 = new URL(window.location.href);
    clearMapFilterUrlParams(url_0);
    window.history.replaceState({}, '', url_0.toString());
  }, []);
  useEffect(() => {
    if (!mapData || !mapCategories) return;
    // Wait until preference sync has settled so we know the final value
    // of hasStoredPreferences before deciding whether to show the modal
    if (options?.syncSettled === false) return;
    let cancelled = false;
    const applyFilterState = async () => {
      const filterState = await parseFilterStateFromUrl();
      if (cancelled || !filterState) return;
      if (options?.hasStoredPreferences) {
        setPendingUrlFilterState(filterState);
      } else {
        dispatch(setFilterStateFromUrl(filterState));
        cleanUpFilterUrlParams();
      }
    };
    void applyFilterState();
    return () => {
      cancelled = true;
    };
  }, [dispatch, parseFilterStateFromUrl, cleanUpFilterUrlParams, mapData, mapCategories, options?.hasStoredPreferences, options?.syncSettled]);
  return {
    parseFilterStateFromUrl,
    cleanUpFilterUrlParams,
    pendingUrlFilterState
  };
}